import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomSheet from '../components/BottomSheet/BottomSheet';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import Icon from '../components/Icon/Icon';
import SearchCard from '../components/SearchCard/SearchCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { API_BASE_URL } from '../config/api';
import './AddProductBottomSheet.css';

interface Product {
  product_id: number;
  product_name: string;
  brand: string;
  category: string;
  type: string;
  image_url?: string;
}

interface AddProductBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (productId: number) => void;
}

const AddProductBottomSheet: React.FC<AddProductBottomSheetProps> = ({ 
  isOpen, 
  onClose, 
  onAddProduct 
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetch(`${API_BASE_URL}/api/products`)
        .then((res) => res.json())
        .then((data) => {
          setProducts(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching products:', err);
          setLoading(false);
        });
    }
  }, [isOpen]);

  const filteredProducts = products.filter((product) =>
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleAddProduct = () => {
    if (selectedProduct) {
      onAddProduct(selectedProduct.product_id);
      setSelectedProduct(null);
      setSearchTerm('');
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    onClose();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Product to Cabinet"
      height="80vh"
      className="add-product-bottom-sheet"
    >
      <div className="add-product-content">
        <div className="search-section">
          <div className="search-input-container">
            <div className="search-input-wrapper">
              <div className="search-icon-slot" aria-hidden="true">
                {!searchTerm ? (
                  <Icon name="search" size="small" />
                ) : null}
              </div>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-field"
                aria-label="Search products"
              />
              <div className="search-clear-slot" aria-hidden="true">
                {searchTerm ? (
                  <button type="button" className="clear-search-button" onClick={handleClearSearch} aria-label="Clear search">
                    <Icon name="close" size="small" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="add-product-loading">
            <LoadingSpinner text="Loading products..." size="small" />
          </div>
        ) : (
          <div className="products-list">
            {filteredProducts.length === 0 ? (
              <div className="empty-state">
                <Icon name="search" size="large" />
                <Typography variant="body">No products found</Typography>
                <Typography variant="body-small">Try adjusting your search terms</Typography>
              </div>
            ) : (
              filteredProducts.map((product) => (
                <SearchCard
                  key={product.product_id}
                  product={product}
                  isSelected={selectedProduct?.product_id === product.product_id}
                  onClick={() => handleProductClick(product)}
                />
              ))
            )}
          </div>
        )}
      </div>

      <div className="add-product-footer">
        <Button variant="secondary" onClick={handleClose} className="cancel-button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAddProduct}
          disabled={!selectedProduct}
          className="add-button"
        >
          Add Product
        </Button>
      </div>
    </BottomSheet>
  );
};

export default AddProductBottomSheet;
